import React, { ForwardedRef, ReactNode, useEffect, useState } from "react";
import { Dropdown, Stack } from "react-bootstrap";
import { FiatCurrency } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./CurrencySelectorDropdown.scss";
import DownArrowIcon from "../../icons/DownArrowIcon";
import { fetchIpInfo } from "../../../utils/helpers";
import { IpInfoCountry } from "../../../types";
import CustomModal from "../../CustomModal/CustomModal";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import WarningBadge from "../../WarningBadge/WarningBadge";
import useDisclosure from "../../../hooks/useDisclosure";
import WalletClosedIcon from "../../icons/WalletClosedIcon";

interface CustomToggleProps {
  children: ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const CustomToggle = React.forwardRef<HTMLButtonElement, CustomToggleProps>(
  ({ children, onClick }, ref: ForwardedRef<HTMLButtonElement>) => (
    <button ref={ref} onClick={(e) => onClick(e)} className="toggle">
      {children}
      <DownArrowIcon fill="white" />
    </button>
  )
);

interface CurrencyChangeConfirmationModalProps extends GenericModalProps {
  newCurrency: FiatCurrency | string;
  handleConfirmChange?: () => void;
}

function CurrencyChangeConfirmationModal({
  isOpen,
  onClose,
  newCurrency,
  handleConfirmChange,
}: CurrencyChangeConfirmationModalProps) {
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      customClass="currency-change-confirmation-modal"
    >
      <Stack gap={4}>
        <Stack className="align-items-center main-content">
          <span className="wallet-icon-wrapper">
            <WalletClosedIcon />
          </span>
          <h2 className="main-copy">{t("confirm_currency")}</h2>
          <p className="sub-copy">
            {t("you_have_selected_currency_this_would_be_used_for", {
              currency: newCurrency,
            })}{" "}
            <span className="fw-bold">{t("deposits")}</span> and{" "}
            <span className="fw-bold">{t("cash_outs")}</span>.
          </p>
        </Stack>
        <Stack gap={3}>
          <WarningBadge
            text={`${t("you_cannot_change_this_after_your_first_top_up")}.`}
            containerClassName="info-text-wrapper"
          />
          <PrimaryActionButton
            size="lg"
            text={t("confirm_specific_currency", { currency: newCurrency })}
            onClick={handleConfirmChange}
          />
        </Stack>
      </Stack>
    </CustomModal>
  );
}

interface CurrencySelectorDropdownProps {
  selectedCurrency?: FiatCurrency;
  setSelectedCurrency: React.Dispatch<
    React.SetStateAction<FiatCurrency | undefined>
  >;
  showConfirmationModal?: boolean;
}

const FIAT_CURRENCIES = Object.values(FiatCurrency);

export default function CurrencySelectorDropdown({
  selectedCurrency,
  setSelectedCurrency,
  showConfirmationModal,
}: CurrencySelectorDropdownProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [temporarySelectedCurrency, setTemporarySelectedCurrency] =
    useState<FiatCurrency>();

  const handleCurrencySelection = (currency: FiatCurrency) => {
    if (showConfirmationModal) {
      setTemporarySelectedCurrency(currency);
      onOpen();
    } else {
      setSelectedCurrency(currency);
    }
  };

  const handleConfirmChange = () => {
    setSelectedCurrency(temporarySelectedCurrency);
    onClose();
  };

  useEffect(() => {
    const getCurrentCountry = async () => {
      const currentCountry = (await fetchIpInfo())?.country;
      if (currentCountry && currentCountry !== IpInfoCountry.NG) {
        setSelectedCurrency(FiatCurrency.USD);
      }
      setSelectedCurrency(FiatCurrency.NGN);
    };

    getCurrentCountry();
  }, [setSelectedCurrency]);

  if (!selectedCurrency) return null;

  return (
    <>
      <div className="d-flex justify-content-start">
        <Dropdown className="currency-dropdown">
          <Dropdown.Toggle
            as={CustomToggle}
            id="dropdown-basic"
            className="toggle"
          >
            {selectedCurrency}
          </Dropdown.Toggle>
          <Dropdown.Menu className="menu">
            {FIAT_CURRENCIES.map((currency, i) => (
              <Dropdown.Item
                key={currency}
                onClick={() => handleCurrencySelection(currency)}
                className="item"
                style={{
                  color: currency === selectedCurrency ? "#6C53E8" : "white",
                  marginBottom: i === FIAT_CURRENCIES.length - 1 ? 0 : 12,
                }}
              >
                {currency}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <CurrencyChangeConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        newCurrency={temporarySelectedCurrency || ""}
        handleConfirmChange={handleConfirmChange}
      />
    </>
  );
}
